import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PaymentManager from '../../services/api/PaymentManager';
import {
  AppWebViewMessageTypes,
  ENVIRONMENTS,
  ERROR_CODES,
  ORDER_PAYMENT_STATUSES,
  OrderType,
  PaymentGateway,
  PaymentMethod,
  PaymentStatus,
  PaypalVariant,
} from '../../services/exports/Constants';
import * as stripeJs from '@stripe/stripe-js';
import { StripeConstructorOptions, StripeElementLocale, StripeElementsOptionsMode } from '@stripe/stripe-js';
import {
  chargesStripeDestination,
  loadStripeIfSecure,
  resolveStripeInit,
  toStripeMinorUnits,
} from '../../utils/stripe';
import useStripeElementsExclusions from '../../hooks/payment/useStripeElementsExclusions';
import useStripeCustomerSession from '../../hooks/payment/useStripeCustomerSession';
import { Elements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useSelector } from 'react-redux';
import HelperMethods from '../../services/exports/HelperMethods';
import { StoreInterface } from '../../store/types';
import collect from 'collect.js';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import WebViewResource from '../../services/resources/WebViewResource';
import useTheme from '../../hooks/ui/useTheme';
import { AdyenCheckout, Core, Dropin } from '@adyen/adyen-web/auto';
import { CheckoutFunction } from '../../hooks/payment/useOrderCheckout';
import { PaymentResult } from '../../services/exports/Interfaces';
import { isAdyenPaymentMethodHiddenForOrderMethod } from '../../utils/payment-options-visibility';
import { STRIPE_ELEMENTS_FONTS_OPTION, buildStripeElementsAppearance } from './stripe-elements/appearance';
import useAuth from '../../hooks/global/useAuth';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import useDeepRef from '../../hooks/utility/useDeepRef';
import useCheckout, {
  GenerateOrderReturnUrlFunction,
  OnOrderPaymentErrorFunction,
  OnOrderPaymentSuccessFunction,
  useGenerateReturnUrl,
  useHandleCheckoutResult,
} from '../../hooks/payment/useCheckout';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { PaymentMethodsResponse } from '@adyen/adyen-web';
import { useGiftCardsBasket } from '../../hooks/basket/useGiftCardBasket';
import paymentLog from '../../services/helpers/PaymentLogger';
import useSpreedlyScript from '../../hooks/payment/useSpreedlyScript';
import {
  StripeElementsMountedContext,
  StripeElementsSession,
  StripeElementsSessionContext,
  StripeJsState,
} from './StripeElementsMounted';
import { castAdyenPaymentMethodType, isAdyenPaymentMethod } from '../../services/helpers/adyenPaymentMethod';
import { useHoldTableVisitCheck } from '../../hooks/order/useTableVisitGuard';

type Props = {
  children: any;
};

type StripeSDK = {
  initialize: Promise<stripeJs.Stripe | null> | null;
  paymentIntent: stripeJs.PaymentIntent;
  loadPaymentIntent: (forceCreate?: boolean) => Promise<void>;
  /**
   * Stripe as the primary gateway: Stripe.js as the platform account, for the one row charged there
   * (PayPal). `initialize` is the restaurant's own account, where the Elements run. Null elsewhere.
   */
  platformStripe: Promise<stripeJs.Stripe | null> | null;
  /** Stripe as the primary gateway: <Elements> is in deferred mode and the intent is created at order time. */
  usesDeferredElements: boolean;
};

type AdyenSDK = {
  initialize: Promise<Core | null> | null;
  paymentElement: Dropin | null;
  paymentMethodsResponse: null | PaymentMethodsResponse;
  setPaymentElement: React.Dispatch<React.SetStateAction<Dropin | null>>;
};

type NullSDK = {
  initialize: null;
};

type BaseCoordinatorContext = {
  loading: boolean;
  isOnlinePayment: boolean;
  paymentsInitialized: boolean;
  isPaymentProcessing: boolean;
  basketTotal: number;
  setIsGiftCardCheckout: (value: boolean) => void;
  setIsPaymentProcessing: (value: boolean) => void;
  onlinePaymentsGateway: PaymentGateway | null;
  secondaryGateway: PaymentGateway | null;
  sdks: GatewaySDKs;
};

export type GatewaySDKMap = {
  [PaymentGateway.Adyen]: AdyenSDK;
  [PaymentGateway.Stripe]: StripeSDK;
};

type GatewaySDKs = {
  [PaymentGateway.Adyen]?: AdyenSDK;
  [PaymentGateway.Stripe]?: StripeSDK;
};

export type PaymentCoordinatorContextType = BaseCoordinatorContext & {
  paymentGatewaySDK: AdyenSDK | StripeSDK | NullSDK;
};

type GatewayCoordinatorProps = {
  active: boolean;
  loading: boolean;
  setLoading: (value: boolean) => void;
  isGiftCardCheckout: boolean;
  isPaymentProcessing: boolean;
  setIsPaymentProcessing: (value: boolean) => void;
  basketTotal: number;
  setSdk: (gateway: PaymentGateway, sdk: AdyenSDK | StripeSDK | null) => void;
  setReady: (gateway: PaymentGateway, ready: boolean) => void;
  children: any;
};

export const PaymentCoordinatorContext = React.createContext<PaymentCoordinatorContextType | null>(null);

const NULL_SDK: NullSDK = { initialize: null };

export default function PaymentCoordinator({ children }: Props): ReactElement {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const {
    onlinePaymentsGateway,
    secondaryGateway,
    selectedPaymentMethod,
    hasOnlinePayments,
    paypalVariant,
    isGiftCardCheckout,
    setIsGiftCardCheckout,
  } = usePaymentFlow();

  const isOnlinePayment = useMemo(
    () => Boolean(selectedPaymentMethod) && selectedPaymentMethod !== PaymentMethod.Cash,
    [selectedPaymentMethod],
  );

  // Stripe-as-secondary exists only to back PayPal-via-Stripe; don't spin it up
  // for PayPal-direct (which is gateway-less).
  const isPaypalViaStripe = paypalVariant === PaypalVariant.ViaStripe;
  const isStripeActive =
    hasOnlinePayments &&
    (onlinePaymentsGateway === PaymentGateway.Stripe ||
      (onlinePaymentsGateway === PaymentGateway.Adyen &&
        secondaryGateway === PaymentGateway.Stripe &&
        isPaypalViaStripe));
  const isAdyenActive = hasOnlinePayments && onlinePaymentsGateway === PaymentGateway.Adyen;
  const isSpreedlyActive = hasOnlinePayments && onlinePaymentsGateway === PaymentGateway.Spreedly;

  // Spreedly has no coordinator of its own — its card form and 3DS lifecycle both live off
  // `window.Spreedly`, installed by the iframe-v1 script. Readiness therefore comes straight from
  // the script hook rather than a setReady() call. A failed load still counts as "initialized":
  // the card form renders its own sdk_load_failed message, and blocking render forever would leave
  // the customer staring at a spinner with no explanation.
  const { loaded: spreedlyLoaded, failed: spreedlyFailed } = useSpreedlyScript({ enabled: isSpreedlyActive });

  const [sdks, setSdks] = useState<GatewaySDKs>({});
  const [readiness, setReadiness] = useState<{ [PaymentGateway.Adyen]?: boolean; [PaymentGateway.Stripe]?: boolean }>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  // A table order's visit running out mid-payment waits for the payment (SideEffectsProvider).
  useHoldTableVisitCheck(isPaymentProcessing);

  const { data } = useOrderFlow();
  const { total: giftCardTotal } = useGiftCardsBasket();

  const basketTotal = useMemo(
    () => (isGiftCardCheckout ? giftCardTotal : data.online_payment_total),
    [isGiftCardCheckout, giftCardTotal, data.online_payment_total],
  );

  const setSdk = useCallback((gateway: PaymentGateway, sdk: AdyenSDK | StripeSDK | null) => {
    setSdks((prev) => ({ ...prev, [gateway]: sdk ?? undefined }));
  }, []);

  const setReady = useCallback((gateway: PaymentGateway, ready: boolean) => {
    setReadiness((prev) => ({ ...prev, [gateway]: ready }));
  }, []);

  // When no gateway is active (e.g., cash-only), we still report initialized so
  // PaymentInitializer doesn't block render.
  const paymentsInitialized = useMemo(() => {
    if (!isAdyenActive && !isStripeActive && !isSpreedlyActive) {
      return true;
    }

    return (
      (!isAdyenActive || readiness.adyen === true) &&
      (!isStripeActive || readiness.stripe === true) &&
      // Until the Spreedly script installs `window.Spreedly` there is nothing to tokenize with, so
      // a tap would only reject inside the pay button and silently reset the spinner.
      (!isSpreedlyActive || spreedlyLoaded || spreedlyFailed)
    );
  }, [
    isAdyenActive,
    isStripeActive,
    isSpreedlyActive,
    readiness.adyen,
    readiness.stripe,
    spreedlyLoaded,
    spreedlyFailed,
  ]);

  // Mounted once whenever PayPal-direct is the active variant, wrapping every payment
  // surface (method rows AND the bottom pay button across web/mobile/native) so the SDK
  // loads a single time and the PayPal buttons can render in the same slot as the other
  // methods' pay button.
  const paypalScriptOptions = useMemo(
    () => ({
      clientId: company?.paypal_standalone?.client_id,
      currency: company?.currency,
      intent: 'capture' as const,
      components: 'buttons' as const,
    }),
    [company?.paypal_standalone?.client_id, company?.currency],
  );

  const context = useMemo<PaymentCoordinatorContextType>(
    () => ({
      loading,
      isOnlinePayment,
      paymentsInitialized,
      isPaymentProcessing,
      basketTotal,
      setIsGiftCardCheckout,
      setIsPaymentProcessing,
      onlinePaymentsGateway: onlinePaymentsGateway ?? null,
      secondaryGateway,
      sdks,
      paymentGatewaySDK: (onlinePaymentsGateway && sdks[onlinePaymentsGateway]) ?? NULL_SDK,
    }),
    [
      loading,
      isOnlinePayment,
      paymentsInitialized,
      isPaymentProcessing,
      basketTotal,
      onlinePaymentsGateway,
      secondaryGateway,
      sdks,
    ],
  );

  const coordinatorProps = {
    loading,
    setLoading,
    isGiftCardCheckout,
    isPaymentProcessing,
    basketTotal,
    setIsPaymentProcessing,
    setSdk,
    setReady,
  };

  const tree = (
    <PaymentCoordinatorContext.Provider value={context}>
      <StripeCoordinator active={isStripeActive} {...coordinatorProps}>
        <AdyenCoordinator active={isAdyenActive} {...coordinatorProps}>
          {children}
        </AdyenCoordinator>
      </StripeCoordinator>
    </PaymentCoordinatorContext.Provider>
  );

  if (!company?.id) {
    return null;
  }

  // Mount the PayPal SDK whenever the restaurant has PayPal-direct configured — keyed on
  // stable company config, NOT paypalVariant. paypalVariant flips Direct→ViaStripe the
  // moment the gift-card pay button sets isGiftCardCheckout, and gating the wrapper on it
  // would change the root element type mid-checkout, remounting the whole subtree (incl.
  // StripeCoordinator) and wedging payment initialization. The SDK simply goes unused when
  // the variant isn't Direct (PaypalDirectButtons only renders for selected===paypal &&
  // variant===Direct), so keeping it mounted is harmless.
  if (company?.paypal_standalone?.client_id) {
    return <PayPalScriptProvider options={paypalScriptOptions}>{tree}</PayPalScriptProvider>;
  }

  return tree;
}

function StripeCoordinator({
  active,
  loading,
  setLoading,
  setSdk,
  setReady,
  basketTotal,
  isGiftCardCheckout,
  isPaymentProcessing,
  children,
}: GatewayCoordinatorProps) {
  const {
    selectedPaymentMethod,
    hasOnlinePayments,
    onlinePaymentsGateway,
    secondaryGateway,
    paypalVariant,
    markPaymentMethodUnavailable,
    isSavedCardSelected,
  } = usePaymentFlow();
  const { isMobileApp } = useMobileApp();
  const theme = useTheme();
  const { t, i18n } = useTranslation(null, { keyPrefix: 'Components:Payment:PaymentCoordinator' });
  // Read through a ref: `t` is not a stable identity everywhere (the test double hands out a new one
  // per render), and loadPaymentIntent feeds the SDK effect, so a changing dependency would re-run
  // setSdk on every render and loop.
  const tRef = useRef(t);
  tRef.current = t;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const [paymentIntent, setPaymentIntent] = useState(null);
  // Bounds the self-heal retries below so a genuinely failing intent creation can't
  // hammer the API in a loop.
  const selfHealAttemptsRef = useRef(0);
  // Methods Stripe refused an intent for. The provider withdraws them from the options, but
  // the selection takes a render to move on, and the self-heal effect must not re-ask meanwhile.
  const refusedMethodsRef = useRef<PaymentMethod[]>([]);

  // When Stripe is the secondary gateway it only ever serves PayPal — so the intent
  // can be created once on activation with payment_method_id=paypal and reused for the
  // whole session. Recreating it on selection changes would force <Elements> (and the
  // Adyen Dropin nested under it) to remount.
  const isStripeSecondary =
    onlinePaymentsGateway === PaymentGateway.Adyen && secondaryGateway === PaymentGateway.Stripe;

  // As the primary gateway, Stripe creates the intent at order time, on the restaurant's account.
  // Only the rows the Elements do not confirm take one up front: PayPal through Stripe (the platform
  // account; PayPal-direct is captured by its own buttons and needs none), the card on file
  // (confirmed with its stored id) and the wallets the native app confirms through its bridge.
  const isNativeWallet =
    isMobileApp &&
    (selectedPaymentMethod === PaymentMethod.ApplePay || selectedPaymentMethod === PaymentMethod.GooglePay);
  const isPaypalThroughStripe =
    selectedPaymentMethod === PaymentMethod.Paypal && paypalVariant !== PaypalVariant.Direct;
  const needsUpfrontIntent = isStripeSecondary || isPaypalThroughStripe || isSavedCardSelected || isNativeWallet;

  // The method the intent is made for: the secondary gateway's one PayPal intent serves every
  // selection, everything else takes its own.
  const intentMethod = isStripeSecondary ? PaymentMethod.Paypal : selectedPaymentMethod;

  // One intent request at a time, told by a ref (the `loading` state reaches the next render only):
  // the mount effect and the self-heal effect fire in the same commit and used to make two intents.
  // A selection that moves while a request is in flight is served once it is done (below), by the
  // method the last request was made for. Skipped, the row under the new selection kept the previous
  // method's intent: a "Credit Card" row drew a neutral intent's Element, with every method the
  // account offers in it (SEPA, PayPal, Pay by Bank…).
  const intentRequestInFlightRef = useRef(false);
  const requestedIntentMethodRef = useRef<PaymentMethod | null | undefined>(undefined);
  // The method the intent on hand was made for.
  const intentForMethodRef = useRef<PaymentMethod | null | undefined>(undefined);

  // Read when a request answers: the row it was made for may have handed the selection to one that
  // takes no intent up front (the saved card's request still in flight when the Element's card row
  // was picked), and its answer must not land under that row.
  const needsUpfrontIntentRef = useRef(needsUpfrontIntent);
  needsUpfrontIntentRef.current = needsUpfrontIntent;

  const loadPaymentIntent = useCallback(
    async (forceCreate: boolean = false) => {
      if (!active) {
        return;
      }

      // Ahead of the one-at-a-time guard: a row that takes no intent clears the one on hand at once,
      // even while another row's request is still in flight.
      if (!needsUpfrontIntent) {
        setPaymentIntent(null);

        return;
      }

      if (loading || intentRequestInFlightRef.current) {
        return;
      }

      const paymentMethodForIntent = intentMethod;

      if (refusedMethodsRef.current.includes(paymentMethodForIntent)) {
        return;
      }

      setLoading(true);
      intentRequestInFlightRef.current = true;
      requestedIntentMethodRef.current = paymentMethodForIntent;

      const body = {
        company_id: company?.id,
        payment_method_id: paymentMethodForIntent,
        force_payment_method: true,
      };

      const isUpdate = paymentIntent?.id && !forceCreate;
      const { response, success, status } = await (
        isUpdate
          ? PaymentManager.updateStripePaymentIntent(paymentIntent.id, body)
          : PaymentManager.createStripePaymentIntent(body)
      ).finally(() => {
        intentRequestInFlightRef.current = false;
      });

      if (success && !needsUpfrontIntentRef.current) {
        intentForMethodRef.current = undefined;
        setPaymentIntent(null);
      } else if (success) {
        intentForMethodRef.current = paymentMethodForIntent;
        setPaymentIntent(response.data);
      } else {
        // Stripe refused the method and the intent on hand is another method's: kept, this row would
        // be paid with it. Dropped, the row waits for the self-heal below to make its own.
        if (paymentIntent?.id && intentForMethodRef.current !== paymentMethodForIntent) {
          setPaymentIntent(null);
        }

        paymentLog.error('stripe', isUpdate ? 'payment_intent_update_failed' : 'payment_intent_create_failed', {
          company_id: company?.id,
          payment_method_id: paymentMethodForIntent,
          status,
          response,
        });

        // A method Stripe will not take (PayPal on an account without the capability) leaves no
        // intent to pay it with, and on the secondary gateway no <Elements> either, so the
        // initializer spins forever. Withdraw the method: the provider re-selects and this effect
        // runs again. The mount effect and the self-heal effect fire together, so two requests can be
        // in flight for the same method; the second refusal must not report it again.
        if (paymentMethodForIntent && !refusedMethodsRef.current.includes(paymentMethodForIntent)) {
          refusedMethodsRef.current = [...refusedMethodsRef.current, paymentMethodForIntent];
          markPaymentMethodUnavailable(paymentMethodForIntent);
          toast.error(tRef.current('method_unavailable'));
        }
      }

      setTimeout(() => setLoading(false), 100);
    },
    [
      active,
      loading,
      company?.id,
      paymentIntent?.id,
      selectedPaymentMethod,
      isStripeSecondary,
      isMobileApp,
      paypalVariant,
      markPaymentMethodUnavailable,
      needsUpfrontIntent,
      intentMethod,
    ],
  );

  useEffect(() => {
    if (!active) return;
    if (!hasOnlinePayments) return;

    if (isStripeSecondary) {
      // Eager once-only: create the PayPal intent as soon as Stripe-secondary activates
      // and don't recreate it on selectedPaymentMethod changes.
      if (!paymentIntent) {
        loadPaymentIntent(true);
      }

      return;
    }

    loadPaymentIntent(true);
  }, [active, selectedPaymentMethod, isStripeSecondary, needsUpfrontIntent]);

  // The selection moved while the last intent was being made: make the one it needs now. Not for the
  // secondary gateway's PayPal intent, which serves every selection (constraint above), nor for a
  // method Stripe refused.
  useEffect(() => {
    if (!active || loading || intentRequestInFlightRef.current || !paymentIntent || isStripeSecondary) {
      return;
    }

    if (requestedIntentMethodRef.current === intentMethod) {
      return;
    }

    if (intentMethod && refusedMethodsRef.current.includes(intentMethod)) {
      return;
    }

    loadPaymentIntent(true);
  }, [active, loading, paymentIntent, intentMethod]);

  // Self-heal: if Stripe is active but ended up with no payment intent and isn't
  // mid-creation, (re)create one. Covers a dropped in-flight creation (e.g. the subtree
  // remounted during init) that the primary effect above won't retry because its deps
  // haven't changed. Bounded by selfHealAttemptsRef to avoid hammering the API.
  useEffect(() => {
    if (!active || !hasOnlinePayments || paymentIntent || loading || !needsUpfrontIntent) {
      return;
    }

    if (selfHealAttemptsRef.current >= 3) {
      return;
    }

    selfHealAttemptsRef.current += 1;
    loadPaymentIntent(true);
  }, [active, hasOnlinePayments, paymentIntent, loading, needsUpfrontIntent]);

  // Reset the self-heal budget once we have a live intent, so a later forced
  // re-creation can self-heal again if needed.
  useEffect(() => {
    if (paymentIntent) {
      selfHealAttemptsRef.current = 0;
    }
  }, [paymentIntent]);

  // As the primary gateway Stripe runs where the restaurant's charges are made: its own account for
  // direct charges (the Elements, the card on file and the native bridge; PayPal, the one platform
  // charge left, gets its own instance below), the platform for destination charges, with the
  // restaurant as the Elements' on_behalf_of. As the secondary gateway it runs where the backend put
  // the PayPal intent: on the platform, unless the intent names an account.
  const isDestinationCharge = chargesStripeDestination(company);
  const { stripeAccount: restaurantStripeAccount, onBehalfOf } = resolveStripeInit(company);
  const stripeAccount: null | string = !isStripeSecondary
    ? restaurantStripeAccount
    : (paymentIntent?.stripe_account ?? null);

  // The native app confirms a wallet against the intent the shop created up front, which lives
  // wherever the backend put it (the readiness stamp decides for Apple Pay). Its SDK has to be told
  // that account, not the one the Elements run as.
  const bridgeStripeAccount: null | string =
    isNativeWallet && paymentIntent?.stripe_account !== undefined
      ? (paymentIntent.stripe_account ?? null)
      : stripeAccount;

  // Bumped to re-create the Elements, and to ask for Stripe.js again where it failed (SHOP-WEB-47).
  const [elementsAttempt, setElementsAttempt] = useState(0);

  const stripePromise = useMemo<null | Promise<stripeJs.Stripe>>(
    () =>
      !active || !company?.service_provider?.stripe_key || (isStripeSecondary && !paymentIntent)
        ? null
        : loadStripeIfSecure(
            company?.service_provider?.stripe_key,
            collect({})
              .when(!!stripeAccount, (options) => options.put('stripeAccount', stripeAccount))
              .all() as StripeConstructorOptions,
          ),
    [
      active,
      company?.service_provider?.stripe_key,
      stripeAccount,
      !isStripeSecondary || paymentIntent !== null,
      elementsAttempt,
    ],
  );

  const [stripeJsState, setStripeJsState] = useState<StripeJsState>('loading');

  useEffect(() => {
    if (!stripePromise) {
      return;
    }

    let current = true;
    setStripeJsState('loading');

    stripePromise.then(
      (stripe) => current && setStripeJsState(stripe ? 'loaded' : 'failed'),
      (error) => {
        if (!current) {
          return;
        }

        setStripeJsState('failed');
        paymentLog.error('stripe', 'stripe_js_load_failed', {
          company_id: company?.id,
          elements_attempt: elementsAttempt,
          message: error?.message,
        });
      },
    );

    return () => {
      current = false;
    };
  }, [stripePromise]);

  const platformStripePromise = useMemo<null | Promise<stripeJs.Stripe>>(
    () =>
      active &&
      !isStripeSecondary &&
      isPaypalThroughStripe &&
      !isDestinationCharge &&
      company?.service_provider?.stripe_key
        ? loadStripeIfSecure(company.service_provider.stripe_key)
        : null,
    [active, isStripeSecondary, isPaypalThroughStripe, isDestinationCharge, company?.service_provider?.stripe_key],
  );

  const { excludedPaymentMethodTypes } = useStripeElementsExclusions(isGiftCardCheckout);
  const { isLoggedIn } = useAuth();
  const customerId = useSelector((state: StoreInterface) => state.profile.profile?.id);
  const [walletSheetOpen, setWalletSheetOpen] = useState(false);
  // An open Apple Pay / Google Pay sheet is a payment under way before it is confirmed (only then is
  // isPaymentProcessing set): a table visit running out waits for it too (SideEffectsProvider).
  useHoldTableVisitCheck(walletSheetOpen);
  const retryBlockedRef = useDeepRef(isPaymentProcessing || walletSheetOpen);
  const elementsAttemptRef = useDeepRef(elementsAttempt);
  const customerSession = useStripeCustomerSession(
    active && !isStripeSecondary && isLoggedIn,
    company?.id,
    customerId,
    // Coming back from a wallet's sheet or pop-up fires focus and visibilitychange, which is when an
    // expired secret is refreshed; that re-creates the Elements under the sheet (SHOP-WEB-45).
    { holdRefresh: isPaymentProcessing || walletSheetOpen },
  );

  // Deferred mode: keyed on the company, its account and the customer session's generation, never on
  // an intent. Amount changes (tip, promo code, delivery fee) re-render the provider, which updates the
  // Elements in place. A zero total (free order, cash) still needs a positive amount to mount; the
  // Element is not what charges it, and the pay button never reaches Stripe for it.
  //
  // No card consent here: saving is Stripe's own box (the CustomerSession), the Element only produces
  // a ConfirmationToken, and the backend confirms the intent with that token, which carries the
  // consent. No paymentMethodCreation either: that is createPaymentMethod's, and a token needs none.
  const currency = (typeof company?.currency === 'string' ? company.currency : 'eur').toLowerCase();
  // The online total is the card's price. A wallet the Express Checkout Element seated (a tap on its
  // button selects it, see ExpressCheckout) is priced by its own draft once that is back, and its
  // sheet charges that: what the breakdown shows is what the customer approves.
  const { data: draft, isDraftCurrent } = useOrderFlow();
  const isWebWalletSelected =
    !isStripeSecondary &&
    !isMobileApp &&
    (selectedPaymentMethod === PaymentMethod.ApplePay ||
      selectedPaymentMethod === PaymentMethod.GooglePay ||
      selectedPaymentMethod === PaymentMethod.Link);
  const elementsTotal =
    !isGiftCardCheckout && isWebWalletSelected && isDraftCurrent && typeof draft?.total === 'number'
      ? draft.total
      : basketTotal;
  const amountInMinorUnits = toStripeMinorUnits(elementsTotal ?? 0, currency);
  const stripeLocale = (i18n?.language ?? 'auto').split('-')[0] as StripeElementLocale;
  const deferredElementsOptions = useMemo<StripeElementsOptionsMode>(
    () => ({
      mode: 'payment',
      amount: amountInMinorUnits > 0 ? amountInMinorUnits : toStripeMinorUnits(1, currency),
      currency,
      excludedPaymentMethodTypes,
      // The Element renders much of Stripe's own copy now; it speaks the shop's language.
      locale: stripeLocale,
      // The checkout draws one placeholder for the whole payment block until it is ready
      // (StripeElementsPaymentMethods); Stripe's own skeleton would be a second one inside it.
      loader: 'never',
      ...STRIPE_ELEMENTS_FONTS_OPTION,
      appearance: buildStripeElementsAppearance(theme),
      ...(customerSession.clientSecret ? { customerSessionClientSecret: customerSession.clientSecret } : {}),
      ...(onBehalfOf ? { onBehalfOf } : {}),
    }),
    [
      onBehalfOf,
      amountInMinorUnits,
      currency,
      excludedPaymentMethodTypes,
      stripeLocale,
      theme.isDarkMode,
      theme.color.brand.text.primary,
      customerSession.clientSecret,
    ],
  );

  const [blockPending, setBlockPending] = useState(false);

  const retryElements = useCallback(
    (reason: 'auto' | 'manual') => {
      if (retryBlockedRef.current) {
        return false;
      }

      paymentLog.info('stripe', 'payment_elements_recreated', {
        company_id: company?.id,
        reason,
        elements_attempt: elementsAttemptRef.current + 1,
      });
      setElementsAttempt(elementsAttemptRef.current + 1);

      return true;
    },
    [company?.id],
  );

  const elementsSession = useMemo<StripeElementsSession>(
    () => ({
      pending: Boolean(stripePromise) && !customerSession.ready,
      withCustomerSession: customerSession.clientSecret !== null,
      dropCustomerSession: customerSession.drop,
      blockPending,
      setBlockPending,
      amount: amountInMinorUnits,
      setWalletSheetOpen,
      stripeJs: stripeJsState,
      retryElements,
      elementsAttempt,
      elementsOptions: deferredElementsOptions,
    }),
    [
      stripePromise,
      customerSession.ready,
      customerSession.clientSecret,
      customerSession.drop,
      blockPending,
      amountInMinorUnits,
      stripeJsState,
      retryElements,
      elementsAttempt,
      deferredElementsOptions,
    ],
  );

  useEffect(() => {
    if (!stripePromise || !isMobileApp) return;

    stripePromise.then(() => {
      // Always the provider's key: it is the publishable key of the account the partner charges on
      // (the master's under platform custody), so an account-less load runs on that platform. The
      // shop's own env key named the FoodAmigos platform, wrong for a partner with its own keys.
      WebViewResource.sendMessage(AppWebViewMessageTypes.LoadStripe, {
        stripe_key: company?.service_provider.stripe_key,
        stripe_account_id: bridgeStripeAccount ?? null,
      });
    });
  }, [stripePromise, isMobileApp, bridgeStripeAccount, company?.service_provider?.stripe_key]);

  useEffect(() => {
    if (!active) {
      setReady(PaymentGateway.Stripe, false);
      setSdk(PaymentGateway.Stripe, null);

      return;
    }

    // The Elements checkout is ready to draw without Stripe.js too: where it may not load (a live key
    // on a plain-HTTP page, a provider without a key) it draws its cash-only fallback, not a
    // placeholder that never goes away.
    (!isStripeSecondary || !!stripePromise) && setReady(PaymentGateway.Stripe, true);
  }, [active, stripePromise, isStripeSecondary]);

  useEffect(() => {
    if (!active) return;

    setSdk(PaymentGateway.Stripe, {
      initialize: stripePromise,
      paymentIntent,
      loadPaymentIntent,
      platformStripe: platformStripePromise,
      usesDeferredElements: !isStripeSecondary,
    });
  }, [active, stripePromise, paymentIntent, loadPaymentIntent, platformStripePromise, isStripeSecondary]);

  if (!active) {
    return children;
  }

  if (isStripeSecondary) {
    return (
      <StripeSecondaryElements
        stripePromise={stripePromise}
        paymentIntent={paymentIntent}
        elementsKey={`company-${company?.id}-${stripeAccount ?? 'platform'}-${paymentIntent?.client_secret}`}
      >
        {children}
      </StripeSecondaryElements>
    );
  }

  // One tree shape from the first render on, so nothing below remounts when the Elements arrive.
  // Stripe is handed over once the customer session has answered (the Elements read it at creation
  // only); until then the checkout draws its placeholders (`pending`), not its no-Stripe fallback.
  const mounted = Boolean(stripePromise) && customerSession.ready;

  return (
    <Elements
      key={`company-${company?.id}-${stripeAccount ?? 'platform'}-deferred-${customerSession.generation}-${elementsAttempt}`}
      stripe={mounted ? stripePromise : null}
      options={deferredElementsOptions}
    >
      <StripeElementsSessionContext.Provider value={elementsSession}>
        <StripeElementsMountedContext.Provider value={mounted}>{children}</StripeElementsMountedContext.Provider>
      </StripeElementsSessionContext.Provider>
    </Elements>
  );
}

/**
 * Stripe as the secondary gateway of an Adyen restaurant, there only for PayPal: the <Elements> sit on
 * the one PayPal intent, around the Adyen Drop-in, and confirm it with the billing address the PayPal
 * row collects (StripePayButton).
 */
function StripeSecondaryElements({
  stripePromise,
  paymentIntent,
  elementsKey,
  children,
}: {
  stripePromise: null | Promise<stripeJs.Stripe>;
  paymentIntent: null | stripeJs.PaymentIntent;
  elementsKey: string;
  children: any;
}) {
  const theme = useTheme();

  if (!stripePromise || !paymentIntent) {
    return children;
  }

  return (
    <Elements
      key={elementsKey}
      stripe={stripePromise}
      options={{
        clientSecret: paymentIntent.client_secret,
        appearance: {
          theme: theme.isDarkMode ? 'night' : 'stripe',
          variables: {
            colorPrimary: theme.color.brand.text.primary,
            colorText: theme.color.brand.text.primary,
          },
        },
      }}
    >
      <StripeElementsMountedContext.Provider value={true}>{children}</StripeElementsMountedContext.Provider>
    </Elements>
  );
}

function AdyenCoordinator({
  active,
  isGiftCardCheckout,
  isPaymentProcessing,
  basketTotal,
  setIsPaymentProcessing,
  setSdk,
  setReady,
  children,
}: GatewayCoordinatorProps) {
  const { i18n } = useTranslation();
  const { t } = useTranslation(null, { keyPrefix: 'Components:Payment:Buttons:PayButton' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { selectedPaymentMethod } = usePaymentFlow();
  const [paymentElement, setPaymentElement] = useState<Dropin | null>(null);
  const [paymentMethodsResponse, setPaymentMethodsResponse] = useState(null);
  const orderType = isGiftCardCheckout ? OrderType.GiftCard : OrderType.Default;

  const { isGuest } = useAuth();
  const { generateReturnUrl } = useGenerateReturnUrl(orderType);

  const { onPaymentError, onPaymentSuccess } = useHandleCheckoutResult(orderType, {
    setLoading: setIsPaymentProcessing,
  });
  const { checkout } = useCheckout(orderType, {
    loading: isPaymentProcessing,
    setLoading: setIsPaymentProcessing,
    onMobileAppCheckoutError: () => null,
  });

  const generateReturnUrlRef = useDeepRef<GenerateOrderReturnUrlFunction>(generateReturnUrl);
  const checkoutRef = useDeepRef<CheckoutFunction>(checkout);
  const onPaymentSuccessRef = useDeepRef<OnOrderPaymentSuccessFunction>(onPaymentSuccess);
  const onPaymentErrorRef = useDeepRef<OnOrderPaymentErrorFunction>(onPaymentError);
  // Last submitted method type — fallback for onError/onPaymentFailed callbacks that
  // fire without a component (e.g. script/network errors).
  const lastMethodTypeRef = useRef<string | undefined>(undefined);
  // Read inside the Drop-in callbacks, which are created once per Drop-in and would otherwise keep
  // the selection from when it was built.
  const selectedPaymentMethodRef = useDeepRef<null | PaymentMethod>(selectedPaymentMethod);
  const isGiftCardCheckoutRef = useDeepRef<boolean>(isGiftCardCheckout);
  const tRef = useDeepRef(t);

  useEffect(() => {
    if (!active) return;
    if (!company?.id || !basketTotal) return;

    loadPaymentMethodsConfig();
  }, [active, company?.id, basketTotal, isGuest, cached_order?.method]);

  async function loadPaymentMethodsConfig() {
    const { success, response, status } = await PaymentManager.getAdyenPaymentMethodsConfig({
      company_id: company?.id,
      shopper_locale: i18n.language,
      amount: {
        value: Number((basketTotal * 100).toFixed(0)),
        currency: company?.currency,
      },
      browser_info: {
        acceptHeader: navigator.userAgent,
        colorDepth: screen.colorDepth,
        javaEnabled: false,
        javaScriptEnabled: true,
        language: navigator.language,
        screenHeight: screen.height,
        screenWidth: screen.width,
        timeZoneOffset: new Date().getTimezoneOffset(),
        userAgent: navigator.userAgent,
      },
    });

    if (success) {
      // The Adyen Drop-in renders whatever methods are in the response, so per-order-method
      // hides (e.g. hide card/wallets on pickup) must be applied here — otherwise hidden
      // online methods still show in the Adyen UI. See FOO-3176.
      const isHidden = (type: string | undefined) =>
        isAdyenPaymentMethodHiddenForOrderMethod(company?.hidden_payment_methods, type, cached_order?.method);

      const priorityOrder = ['applepay', 'googlepay', 'scheme'];
      const sorted = {
        ...response.data,
        ...(response.data.storedPaymentMethods
          ? {
              storedPaymentMethods: response.data.storedPaymentMethods.filter(
                (method: { type?: string }) => !isHidden(method.type),
              ),
            }
          : {}),
        paymentMethods: [...response.data.paymentMethods]
          .filter((method) => !isHidden(method.type))
          .sort((a, b) => {
            const aIndex = priorityOrder.indexOf(a.type);
            const bIndex = priorityOrder.indexOf(b.type);
            const aPriority = aIndex === -1 ? priorityOrder.length : aIndex;
            const bPriority = bIndex === -1 ? priorityOrder.length : bIndex;

            return aPriority - bPriority;
          }),
      };

      setPaymentMethodsResponse(sorted);

      return;
    }

    paymentLog.error('adyen', 'payment_methods_load_failed', {
      company_id: company?.id,
      order_type: orderType,
      basket_total: basketTotal,
      currency: company?.currency,
      status,
      response,
    });
  }

  const confirmPayment = async (order, paymentResponse, actions): Promise<PaymentResult> => {
    try {
      if (paymentResponse.action) {
        paymentLog.info('adyen', 'confirm_payment_pending', {
          order_uuid: order?.uuid,
          company_id: company?.id,
          action_type: paymentResponse.action?.type,
        });
        actions.resolve(paymentResponse);

        return {
          status: PaymentStatus.Pending,
          error: null,
          result: null,
        };
      } else {
        paymentLog.info('adyen', 'confirm_payment_succeeded', {
          order_uuid: order?.uuid,
          company_id: company?.id,
        });
        actions.resolve();

        return {
          status: PaymentStatus.Succeeded,
          error: null,
          result: null,
        };
      }
    } catch (error) {
      paymentLog.issue('adyen', 'confirm_payment_threw', {
        order_uuid: order?.uuid,
        company_id: company?.id,
        message: error?.message,
        name: error?.name,
      });
      actions.reject(error);

      return {
        status: PaymentStatus.Failed,
        error: null,
        result: null,
      };
    }
  };

  const locale = useMemo(
    () => HelperMethods.resolveAdyenLocale(company?.region?.number_locale),
    [company?.region?.number_locale],
  );

  const adyenPromise = useMemo(
    () =>
      !active || !company?.id || !company?.service_provider?.adyen_key || !paymentMethodsResponse || !basketTotal
        ? null
        : AdyenCheckout({
            paymentMethodsResponse: paymentMethodsResponse,
            clientKey: company?.service_provider?.adyen_key,
            environment: import.meta.env.VITE_MODE === ENVIRONMENTS.PROD ? 'live' : 'test',
            showPayButton: true,
            locale,
            countryCode: company?.country,
            amount: {
              value: Number((basketTotal * 100).toFixed(0)),
              currency: company.currency,
            },
            onPaymentCompleted: (result) => {
              paymentLog.info('adyen', 'sdk_payment_completed', {
                company_id: company?.id,
                order_type: orderType,
                result_code: result?.resultCode,
              });
            },
            onPaymentFailed: (result, component) => {
              // The Drop-in passes itself as `component` here (component.type === 'dropin'),
              // which masks the real payment method, so prefer the type captured in onSubmit.
              const componentType = component?.type;
              const methodType =
                componentType && componentType !== 'dropin' ? componentType : lastMethodTypeRef.current;
              const isWallet = methodType === 'applepay' || methodType === 'googlepay';

              const resultCode = result?.resultCode;
              const refusalReason = (result as any)?.refusalReason;
              const refusalReasonCode = (result as any)?.refusalReasonCode;

              const attrs = {
                company_id: company?.id,
                order_type: orderType,
                method_type: methodType,
                result_code: resultCode,
                refusal_reason: refusalReason,
                refusal_reason_code: refusalReasonCode,
                // The typed PaymentFailedData only exposes `resultCode`, but the runtime
                // object an Apple Pay / wallet decline returns may carry more — capture
                // every attribute (the logger serializes objects) so we can see it.
                ...(result && typeof result === 'object'
                  ? { result_payload: { ...(result as Record<string, unknown>) } }
                  : { result_payload: result }),
              };

              // Wallet failures share the Stripe event code so both gateways chart
              // as one series (event:wallet_confirm_failed).
              const event = isWallet ? 'wallet_confirm_failed' : 'sdk_payment_failed';

              // Adyen's advanced flow rejects with no payload on an ordinary decline
              // (bad card details, insufficient funds, backend refusal), so all three
              // fields are blank. Those are expected, shopper-recoverable failures —
              // and the rich context already went out as a `submit_rejected` warn — so
              // log WARN, not an alertable Sentry error. Only promote to ERROR when
              // Adyen actually handed back a failed resultCode (processing/config issue
              // worth paging on). Wallet misconfig (e.g. Apple Pay domain not
              // whitelisted) surfaces via onError → sdk_error, which stays an error.
              const hasResultData = resultCode != null || refusalReason != null || refusalReasonCode != null;

              if (hasResultData) {
                paymentLog.error('adyen', event, attrs);

                return;
              }

              paymentLog.warn('adyen', event, attrs);
            },
            onSubmit: async (state, component, actions) => {
              lastMethodTypeRef.current = state?.data?.paymentMethod?.type;

              const checkoutSessionId =
                typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                  ? crypto.randomUUID()
                  : `cs_${Date.now()}_${Math.random().toString(36).slice(2)}`;

              paymentLog.setContext({
                checkout_session_id: checkoutSessionId,
                gateway: 'adyen',
                company_id: company?.id,
                order_type: orderType,
              });

              paymentLog.info('adyen', 'submit', {
                basket_total: basketTotal,
                method_type: state?.data?.paymentMethod?.type,
                method_brand: (state?.data?.paymentMethod as any)?.brand,
              });

              // checkout() blocks until the OTP loop fully settles, so we
              // only touch `actions` once at the very end. Calling
              // actions.resolve/reject earlier surfaces error UI in the Dropin.
              //
              // Adyen-wallet workaround: Apple Pay / Google Pay sheets are OS
              // native and have no API to dismiss without finalizing. If the
              // backend asks for OTP we'd otherwise be stuck with the wallet
              // sheet on top, blocking the OTP modal. As a workaround we
              // fake-resolve the wallet sheet with `Authorised` so it
              // dismisses — the real charge then happens via the OTP retry
              // path which creates a NEW order with the captured wallet data.
              const methodType = state?.data?.paymentMethod?.type;
              const isWalletPayment = methodType === 'applepay' || methodType === 'googlepay';
              let walletDismissedForOtp = false;

              // The Drop-in's own pay and wallet buttons submit without going through our selection,
              // which is null once it mounts, so the method comes from what the shopper actually used.
              // Gift cards are charged with the selection regardless (useGiftCardCheckout), so they are
              // left as they were. The selection is not updated here: it is a draft dependency, and a
              // redraft mid-payment would reprice the order under the Drop-in.
              const isOrderCheckout = !isGiftCardCheckoutRef.current;
              const previousSelection = selectedPaymentMethodRef.current;
              const paymentMethodId =
                castAdyenPaymentMethodType(methodType) ??
                (isAdyenPaymentMethod(previousSelection) ? previousSelection : null);

              if (isOrderCheckout && !paymentMethodId) {
                paymentLog.issue('adyen', 'submit_unmapped_payment_method', {
                  company_id: company?.id,
                  order_type: orderType,
                  method_type: methodType,
                  selected_payment_method: previousSelection,
                });
                toast.error(tRef.current('toasts.select_payment_method'));
                actions.reject();

                return;
              }

              const baseReturnUrl = generateReturnUrlRef.current();
              const separator = baseReturnUrl.includes('?') ? '&' : '?';
              const returnUrl = `${baseReturnUrl}${separator}checkout_session_id=${encodeURIComponent(checkoutSessionId)}`;

              let lastPaymentResponse: any = null;
              let confirmPaymentCalled = false;

              const result = await checkoutRef.current(
                (order, paymentIntent) => {
                  confirmPaymentCalled = true;
                  lastPaymentResponse = paymentIntent;

                  return confirmPayment(order, paymentIntent, actions);
                },
                null,
                {
                  ...(isOrderCheckout
                    ? { payment_method_id: paymentMethodId, payment_method_source: 'adyen_dropin' }
                    : {}),
                  adyen_payment_data: state.data,
                  return_url: returnUrl,
                  checkout_session_id: checkoutSessionId,
                },
                undefined,
                {
                  onBeforeOtpModalOpen: () => {
                    if (!isWalletPayment || walletDismissedForOtp) {
                      return;
                    }

                    walletDismissedForOtp = true;
                    paymentLog.info('adyen', 'wallet_dismissed_for_otp', {
                      company_id: company?.id,
                      order_type: orderType,
                      method_type: methodType,
                    });
                    actions.resolve({ resultCode: 'Authorised' });
                  },
                },
              );

              // If we already consumed `actions` to dismiss the wallet sheet,
              // skip the post-checkout resolve/reject — the actual outcome of
              // the OTP retry is handled inside the recursive checkout via
              // onPaymentSuccess / onPaymentError.
              if (walletDismissedForOtp) {
                return;
              }

              if (result) {
                return actions.resolve({ resultCode: 'Authorised' });
              }

              if (confirmPaymentCalled) {
                paymentLog.info('adyen', 'submit_handed_off_to_dropin', {
                  company_id: company?.id,
                  order_type: orderType,
                  order_uuid: lastPaymentResponse?.order_uuid ?? lastPaymentResponse?.uuid,
                  result_code: lastPaymentResponse?.resultCode,
                  action_type: lastPaymentResponse?.action?.type,
                });

                return;
              }

              paymentLog.warn('adyen', 'submit_rejected', {
                company_id: company?.id,
                order_type: orderType,
                method_type: methodType,
                method_brand: (state?.data?.paymentMethod as any)?.brand,
                basket_total: basketTotal,
                result_code: lastPaymentResponse?.resultCode,
                refusal_reason: lastPaymentResponse?.refusalReason,
                refusal_reason_code: lastPaymentResponse?.refusalReasonCode,
                action_type: lastPaymentResponse?.action?.type,
              });
              actions.reject();
            },
            onError: (error, component) => {
              const componentType = component?.type;
              const methodType =
                componentType && componentType !== 'dropin' ? componentType : lastMethodTypeRef.current;
              const isWallet = methodType === 'applepay' || methodType === 'googlepay';
              const attrs = {
                company_id: company?.id,
                order_type: orderType,
                method_type: methodType,
                name: (error as any)?.name,
                message: (error as any)?.message,
                error_code: (error as any)?.errorCode,
              };

              // A shopper dismissing a wallet sheet / closing the PayPal window surfaces as
              // a CANCEL error — not a failure. Keep it as a breadcrumb, not a Sentry issue.
              if ((error as any)?.name === 'CANCEL') {
                paymentLog.info('adyen', 'payment_cancelled', attrs);

                return;
              }

              // Wallet failures share the Stripe event code so both gateways chart
              // as one series (event:wallet_confirm_failed).
              paymentLog.error('adyen', isWallet ? 'wallet_confirm_failed' : 'sdk_error', attrs);
            },
            onAdditionalDetails: async (state) => {
              const checkoutSessionId = paymentLog.getContext().checkout_session_id as string | undefined;

              paymentLog.info('adyen', 'additional_details', {
                company_id: company?.id,
                order_type: orderType,
              });

              const { success, response, status } = await PaymentManager.submitAdyenPaymentDetails(
                {
                  company_id: company?.id,
                  adyen_payment_data: state.data,
                  ...(checkoutSessionId ? { checkout_session_id: checkoutSessionId } : {}),
                },
                {
                  // Keep the global error modal's "Continue" as a plain dismiss —
                  // without a handler InfoModal falls back to navigating home.
                  [ERROR_CODES.CARD_EXCEPTION]: () => null,
                },
              );

              if (success && response.data.data.payment_status === ORDER_PAYMENT_STATUSES.PAID) {
                paymentLog.info('adyen', 'additional_details_paid', {
                  company_id: company?.id,
                  order_type: orderType,
                  order_uuid: response.data.data?.uuid,
                });

                return onPaymentSuccessRef.current(response.data.data);
              }

              paymentLog.error('adyen', 'additional_details_failed', {
                company_id: company?.id,
                order_type: orderType,
                success,
                status,
                response: response,
              });
              onPaymentErrorRef.current();
            },
          }),
    [
      active,
      company?.service_provider?.adyen_key,
      company?.id,
      company?.currency,
      paymentMethodsResponse,
      basketTotal,
      locale,
    ],
  );

  useEffect(() => {
    if (!active) {
      setReady(PaymentGateway.Adyen, false);
      setSdk(PaymentGateway.Adyen, null);

      return;
    }

    !!adyenPromise && setReady(PaymentGateway.Adyen, true);
  }, [active, adyenPromise]);

  useEffect(() => {
    if (!active) return;

    setSdk(PaymentGateway.Adyen, {
      initialize: adyenPromise,
      paymentElement,
      paymentMethodsResponse,
      setPaymentElement,
    });
  }, [active, adyenPromise, paymentElement]);

  return children;
}
